import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { ADMIN } from "@/constants/testIds";
import { Lock, CheckCircle2, AlertTriangle, ArrowLeft } from "lucide-react";

const MIN_LENGTH = 8;

// Step 2 of password recovery: the Firebase action handler. Reached only via
// the emailed link, which carries a single-use `oobCode`. We verify the code
// before showing the form so an expired link fails fast rather than after the
// user has typed a password.
//
// Requires Firebase Console → Authentication → Templates → Password reset →
// "Customize action URL" pointed at <domain>/admin/reset. Without that the
// link opens Firebase's own (unbranded) page instead, which still works.
export default function ResetPassword() {
  const { verifyResetCode, completePasswordReset } = useAuth();
  const [params] = useSearchParams();
  const navigate = useNavigate();

  const oobCode = params.get("oobCode") || "";
  const mode = params.get("mode");

  // verifying → ready → done, or invalid at any point before submit.
  const [status, setStatus] = useState("verifying");
  const [accountEmail, setAccountEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [invalidReason, setInvalidReason] = useState("");

  useEffect(() => {
    let cancelled = false;
    // The custom action URL receives every email action type; we only handle
    // password resets here.
    if (!oobCode || (mode && mode !== "resetPassword")) {
      setInvalidReason("This password reset link is missing or malformed.");
      setStatus("invalid");
      return undefined;
    }
    verifyResetCode(oobCode)
      .then((email) => {
        if (cancelled) return;
        setAccountEmail(email);
        setStatus("ready");
      })
      .catch((err) => {
        if (cancelled) return;
        const code = err?.code || "";
        setInvalidReason(
          code === "auth/expired-action-code"
            ? "This password reset link has expired. Reset links are valid for one hour."
            : "This password reset link is invalid or has already been used."
        );
        setStatus("invalid");
      });
    return () => {
      cancelled = true;
    };
  }, [oobCode, mode, verifyResetCode]);

  // Bounce to the login page a moment after a successful change.
  useEffect(() => {
    if (status !== "done") return undefined;
    const t = setTimeout(() => navigate("/admin", { replace: true }), 2500);
    return () => clearTimeout(t);
  }, [status, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (password.length < MIN_LENGTH) {
      setError(`Password must be at least ${MIN_LENGTH} characters.`);
      return;
    }
    if (password !== confirm) {
      setError("Passwords do not match.");
      return;
    }
    setBusy(true);
    try {
      await completePasswordReset(oobCode, password);
      setStatus("done");
    } catch (err) {
      const code = err?.code || "";
      if (code === "auth/expired-action-code" || code === "auth/invalid-action-code") {
        setInvalidReason("This password reset link has expired or was already used.");
        setStatus("invalid");
      } else if (code === "auth/weak-password") {
        setError("That password is too weak. Please choose a stronger one.");
        setBusy(false);
      } else {
        setError("Couldn’t update the password. Please try again.");
        // eslint-disable-next-line no-console
        console.error("[admin-reset]", err);
        setBusy(false);
      }
    }
  };

  const shell = (children) => (
    <div className="min-h-screen flex items-center justify-center bg-ink px-6" data-testid={ADMIN.resetPage}>
      <div className="w-full max-w-md border border-bronze/30 bg-cream p-10">
        <div className="flex items-center gap-3 text-bronze">
          <Lock size={18} />
          <span className="text-[0.7rem] tracking-[0.28em] uppercase">A Homely Abode · Admin</span>
        </div>
        {children}
      </div>
    </div>
  );

  if (status === "verifying") {
    return shell(
      <p className="mt-6 text-sm tracking-[0.2em] uppercase text-olive animate-pulse">
        Verifying link…
      </p>
    );
  }

  if (status === "invalid") {
    return shell(
      <>
        <h1 className="font-serif text-3xl text-ink mt-4">Link no longer valid</h1>
        <div
          data-testid={ADMIN.resetInvalid}
          className="mt-6 flex gap-3 border border-red-200 bg-red-50 px-4 py-4 text-sm text-red-800"
        >
          <AlertTriangle size={18} className="shrink-0 mt-0.5" />
          <p>{invalidReason}</p>
        </div>
        <Link to="/admin/forgot" className="btn-primary w-full mt-6 inline-block text-center">
          Request a New Link
        </Link>
        <Link
          to="/admin"
          className="mt-6 inline-flex items-center gap-2 text-sm text-olive hover:text-bronze"
        >
          <ArrowLeft size={14} /> Back to sign in
        </Link>
      </>
    );
  }

  if (status === "done") {
    return shell(
      <>
        <h1 className="font-serif text-3xl text-ink mt-4">Password updated</h1>
        <div
          data-testid={ADMIN.resetSuccess}
          className="mt-6 flex gap-3 border border-bronze/30 bg-beige/40 px-4 py-4 text-sm text-ink"
        >
          <CheckCircle2 size={18} className="shrink-0 text-bronze mt-0.5" />
          <p>Your password has been changed. Redirecting you to sign in…</p>
        </div>
        <Link to="/admin" className="btn-primary w-full mt-6 inline-block text-center">
          Sign In Now
        </Link>
      </>
    );
  }

  return shell(
    <>
      <h1 className="font-serif text-3xl text-ink mt-4">Set a new password</h1>
      <p className="text-sm text-olive mt-1">
        for <span className="text-ink">{accountEmail}</span>
      </p>

      <form onSubmit={handleSubmit} className="mt-8 space-y-5">
        <label className="block">
          <span className="editorial-label">New password</span>
          <input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            data-testid={ADMIN.resetPassword}
            className="editorial-input"
            autoComplete="new-password"
            minLength={MIN_LENGTH}
            required
          />
          <span className="block text-xs text-olive mt-1">
            At least {MIN_LENGTH} characters.
          </span>
        </label>
        <label className="block">
          <span className="editorial-label">Confirm password</span>
          <input
            type="password"
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            data-testid={ADMIN.resetConfirm}
            className="editorial-input"
            autoComplete="new-password"
            required
          />
        </label>

        {error && (
          <p
            data-testid={ADMIN.resetError}
            className="text-sm text-red-700 bg-red-50 border border-red-200 px-3 py-2"
          >
            {error}
          </p>
        )}

        <button
          type="submit"
          disabled={busy}
          data-testid={ADMIN.resetSubmit}
          className="btn-primary w-full disabled:opacity-60"
        >
          {busy ? "Updating…" : "Change Password"}
        </button>
      </form>
    </>
  );
}
